/* eslint-disable */

import axios from 'axios';
axios.defaults.baseURL = process.env.VUE_APP_TEAMIO_API_URL;
import moment from 'moment-timezone';
import Vue from 'vue';
import _ from 'lodash';
import { safeSnapshot } from '@/store/firestoreListen';
import { 
    collection, 
    query, 
    where, 
    addDoc, 
    doc, 
    getDoc,
    updateDoc, 
    deleteDoc, 
    arrayUnion, 
    serverTimestamp 
} from 'firebase/firestore';

function findColumn(board, columnId) { 
    for (const key in board.columns) { 
        if (board.columns.hasOwnProperty(key)) { 
            const column = board.columns[key]; 
            if (column.id === columnId) { 
                return column; 
            } 
        } 
    }
    return null;
}

// Singleton handle for the boards real-time listener so fetchBoards can tear
// down a stale listener before building a new one (e.g. when userID hydrates
// after mount, or on a site switch). Module-scoped rather than Vuex state
// because an unsubscribe is a function, not serializable state.
let _boardsUnsub = null;

export default {

    namespaced: true,
    state: {
        boards: [],
        // Performance optimization state
        performanceFlags: {
            enableMemoryCleanup: true,
            maxBoards: 50, // Reasonable limit for kanban boards
            enablePerformanceMonitoring: true
        }
    },

    mutations: {
        setBoards(state, payload) {            
            // Apply memory management
            let boards = payload;
            if (state.performanceFlags.enableMemoryCleanup && boards.length > state.performanceFlags.maxBoards) {
                boards = boards.slice(0, state.performanceFlags.maxBoards);
            }
            state.boards = boards;
        }
    },

    getters : {   
        getBoard: (state) => (id) => { 
            return state.boards.find(board => board.id === id);     
        },
    },

    actions: {

        // Listen for boards the current user is allowed to see.
        // A board is visible when accessibleBy contains '*' (public board)
        // or the user's EmployeeID (private board where the user is a
        // member). Firestore rules enforce the same predicate server-side,
        // so non-members literally cannot read private boards.
        async fetchBoards({ commit, rootState, dispatch }, {siteID}) {
            // Tear down any previous boards listener so a rebuild REPLACES the
            // stale query instead of running two listeners at once.
            if (_boardsUnsub) {
                try { _boardsUnsub(); } catch (e) { /* already torn down */ }
                _boardsUnsub = null;
            }

            const userEmpID = rootState.customer.userID;

            // GUARD: a private board is only returned when the query carries the
            // viewer's EmployeeID. If userID hasn't hydrated yet, building the
            // listener now yields array-contains-any ['', '*'] — which silently
            // DROPS every private board the user is a member of (public boards
            // still match via '*'), and the listener is never rebuilt. So bail;
            // Kanban.vue watches `userID` and re-dispatches fetchBoards once it
            // is available. (Tanya Rajdev, 2026-06-03: a member who could not
            // see her own private board.)
            if (!userEmpID) {
                dispatch('alert/setLoading', false, {root: true});
                return;
            }

            dispatch('alert/setLoading', true, {root: true});
            const q = query(
                collection(rootState.db, 'kanban-boards'),
                where('siteID', '==', siteID),
                where('accessibleBy', 'array-contains-any', [userEmpID, '*'])
            );
            _boardsUnsub = safeSnapshot(q, (snapshot) => {
                const _boards = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                // Use debounced update instead of direct commit
                dispatch('debouncedUpdateBoards', _boards);

                dispatch('alert/setLoading', false, {root: true});
            }, {
                source: 'kanban/boards',
                onError: (error, { denied }) => {
                    console.error('Error fetching boards:', error);
                    // A denial is handled by the session-health guard (src/services/sessionHealth.js),
                    // never painted raw. Still clear the spinner: setErrored used to do it as a side effect.
                    if (denied) {
                        dispatch('alert/setLoading', false, {root: true});
                        return;
                    }
                    dispatch('alert/setErrored', true, {root: true});
                    dispatch('alert/setMessage', error, {root: true});
                }
            });
            // Store the unsubscribe function to remove the listener later if needed
            commit('customer/addFirestoreListener', _boardsUnsub, {root: true});
        },
        
        // Debounced update function to prevent rapid state changes
        debouncedUpdateBoards: _.debounce(function({ commit, state }, boards) {
            commit('setBoards', boards);
        }, 250),

        // Creates a board with visibility metadata. Payload may include
        // `memberEmployeeIDs: [...]` for a private board; otherwise the
        // board is public. Creator's EmpID is auto-included in private
        // boards so they always retain access.
        //
        // Stored shape:
        //   accessibleBy: ['*']                           -> public
        //   accessibleBy: [creatorEmpID, ...others]       -> private
        async createBoard({ commit, rootState }, payload) {
            try {
                const userEmpID = rootState.customer.userID;
                const members = Array.isArray(payload.memberEmployeeIDs) ? payload.memberEmployeeIDs : [];
                const isPrivate = members.length > 0;
                const accessibleBy = isPrivate
                    ? Array.from(new Set([userEmpID, ...members].filter(Boolean)))
                    : ['*'];

                // Strip the convenience field; persist only the canonical shape.
                // createdAt/updatedAt: the dashboard's Recent Boards list sorts by
                // these — without them a new board sorts to epoch-0 and never
                // surfaces once 5+ boards have card activity (Tanya/Allay
                // 2026-06-11: "my restricted board doesn't save").
                // Mirrored in cypress/node-tests/kanban-recent-boards.test.js.
                const { memberEmployeeIDs, ...rest } = payload;
                const docPayload = {
                    ...rest,
                    accessibleBy,
                    createdByEmployeeID: userEmpID,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                };

                const docRef = await addDoc(collection(rootState.db, 'kanban-boards'), docPayload);
                return { id: docRef.id, ...docPayload };
            } catch (error) {
                throw error;
            }
        },

        // Replaces a board's membership. Pass `members: []` to make the
        // board public again. Membership cannot be emptied for a private
        // board (Firestore rules enforce this too); callers wanting to
        // "remove the last member" should pass an empty list which converts
        // back to public.
        async updateBoardMembership({ rootState }, { boardId, members }) {
            try {
                const userEmpID = rootState.customer.userID;
                const cleaned = Array.isArray(members) ? members.filter(Boolean) : [];
                const accessibleBy = cleaned.length === 0
                    ? ['*']
                    : Array.from(new Set([userEmpID, ...cleaned]));
                const boardRef = doc(rootState.db, 'kanban-boards', boardId);
                await updateDoc(boardRef, {
                    accessibleBy,
                    updatedAt: serverTimestamp(),
                });
            } catch (error) {
                throw error;
            }
        },

        async deleteBoard({ commit, rootState }, {boardId}) {
            try {
                const boardRef = doc(rootState.db, 'kanban-boards', boardId);
                await deleteDoc(boardRef);                
            } catch (error) {
                throw error;
            }            
        },
        
        async addColumn({ commit, rootState}, { boardId, column }) {             
            try {
                const boardRef = doc(rootState.db, 'kanban-boards', boardId); 
                await updateDoc(boardRef, { columns: arrayUnion(column) });             
            } catch (error) {
                throw error;
            }           
        }, 
        
        async addTask({ commit, rootState }, { boardId, columnId, task, notification }) {
            try {
                const boardRef = doc(rootState.db, 'kanban-boards', boardId);
                const snapshot = await getDoc(boardRef);
                const board = snapshot.data();
                const column = findColumn(board, columnId);
                if (column) {
                    column.tasks.push(task);
                    await updateDoc(boardRef, { columns: board.columns });
                }

                // Write @-mention notifications — one doc per mentioned employee.
                // Mirrors the pattern in store/tasks.js::addPersonalTask for TASK assignments.
                // Failure to write a notification should NOT prevent the card from being saved.
                if (Array.isArray(notification) && notification.length > 0) {
                    for (const n of notification) {
                        try {
                            n.boardId = boardId;
                            n.columnId = columnId;
                            n.kanbanTaskId = task.id;
                            await addDoc(collection(rootState.db, 'notifications'), n);
                        } catch (e) {
                            try { console.warn('[kanban] Failed to write notification:', e); } catch(_) {}
                        }
                    }
                }
            } catch (err) {
                throw err;
            }
        },

        async updateBoard({ commit, rootState, state }, {boardId, updates}) {
            try {
                const boardRef = doc(rootState.db, 'kanban-boards', boardId);
                await updateDoc(boardRef, {
                    ...updates,
                    updatedAt: serverTimestamp()
                });
            } catch (error) {
                throw error;
            }
        },

        // Write a single notification doc for a Kanban @-mention. Used by the
        // Edit flow (TaskCard → Kanban.vue → this action). The Add flow passes
        // `notification` as part of the addTask payload and writes all docs
        // there; this action exists so the Edit flow has a clean single-doc
        // entry point.
        async writeKanbanNotification({ rootState }, notification) {
            if (!notification || !notification.employeeID) return;
            try {
                await addDoc(collection(rootState.db, 'notifications'), notification);
            } catch (err) {
                throw err;
            }
        },

        // Persist the board-level "tag alerts" config. Mirrors
        // updateBoardMembership's style exactly (single updateDoc + updatedAt).
        // tagAlerts shape:
        //   { enabled: bool, tags: string[] (normalized lowercase),
        //     recipientEmployeeIDs: EmployeeID[] }
        // This is the user's explicit Save action, so failures PROPAGATE to the
        // caller (Kanban.vue toasts) — unlike the fire-and-forget alert below.
        async updateTagAlerts({ rootState }, { boardId, tagAlerts }) {
            try {
                const boardRef = doc(rootState.db, 'kanban-boards', boardId);
                await updateDoc(boardRef, {
                    tagAlerts,
                    updatedAt: serverTimestamp(),
                });
            } catch (error) {
                throw error;
            }
        },

        // Fire the per-board tag-alert email. The backend (built separately)
        // reads the recipient list from the board doc, so recipients are
        // deliberately NOT sent here — only the context needed to compose and
        // route the email.
        //
        // Swallows ALL errors and NEVER rethrows — same contract as the
        // notification writes in addTask (:225-228). The card has already saved
        // successfully by the time this is dispatched; an alert-email failure
        // must never surface as a card-save error to the user.
        async sendTagAlert(_ctx, payload) {
            try {
                await axios.post('/kanban-tag-alert', payload, { crossdomain: true });
            } catch (err) {
                try { console.warn('[kanban] Failed to send tag alert:', err); } catch (_) {}
            }
        }
    }
};